import webApiClient from './client';

/**
 * Organization Management API Client
 *
 * Handles organization operations:
 * - Settings management
 * - Template operations
 * - Profile management
 */

export const organizationApi = {
  /**
   * Get organization settings
   * GET /api/v2/org/:organisationId/settings
   */
  getSettings: async (organisationId) => {
    const response = await webApiClient.get(`/api/v2/org/${organisationId}/settings`);

    if (!response.ok || !response.data || !response.data.settings) {
      throw new Error(response.data?.message || 'Failed to fetch settings');
    }

    return response.data.settings;
  },

  /**
   * Update organization settings
   * PUT /api/v2/org/:organisationId/settings
   */
  updateSettings: async (organisationId, settingsData) => {
    const response = await webApiClient.put(`/api/v2/org/${organisationId}/settings`, settingsData);

    if (!response.ok || !response.data || !response.data.settings) {
      throw new Error(response.data?.message || 'Failed to update settings');
    }

    return response.data.settings;
  },

  /**
   * Get all available ticket templates
   * GET /api/v2/ticket/templates
   */
  getTicketTemplates: async () => {
    const response = await webApiClient.get('/api/v2/ticket/templates');

    if (!response.ok || !response.data || !response.data.templates) {
      throw new Error(response.data?.message || 'Failed to fetch ticket templates');
    }

    return response.data.templates;
  },

  /**
   * Get specific ticket template by ID
   * GET /api/v2/ticket/templates/:templateId
   */
  getTemplateById: async (templateId) => {
    const response = await webApiClient.get(`/api/v2/ticket/templates?templateId=${templateId}`);

    if (!response.ok || !response.data || !response.data.template) {
      throw new Error(response.data?.message || 'Failed to fetch template');
    }

    return response.data.template;
  },

  /**
   * Apply ticket template to organization
   * POST /api/v2/org/:organisationId/settings/apply-template
   */
  applyTemplate: async (organisationId, templateId) => {
    const response = await webApiClient.post(
      `/api/v2/org/${organisationId}/settings/apply-template`,
      { templateId }
    );

    if (!response.ok || !response.data) {
      throw new Error(response.data?.message || 'Failed to apply template');
    }

    return response.data;
  },

  /**
   * Get organisation profile (top-level org document)
   * GET /api/v2/org/:organisationId
   */
  getOrgProfile: async (organisationId) => {
    const response = await webApiClient.get(`/api/v2/org/${organisationId}`);

    if (!response.ok || !response.data?.organisation) {
      throw new Error(response.data?.message || 'Failed to fetch profile');
    }

    return response.data.organisation;
  },

  /**
   * Update organisation profile (name, logo, location, blurb, dance styles)
   * PATCH /api/v2/org/:organisationId
   */
  updateOrgProfile: async (organisationId, profileData) => {
    const response = await webApiClient.patch(`/api/v2/org/${organisationId}`, profileData);

    if (!response.ok || !response.data?.organisation) {
      throw new Error(response.data?.message || 'Failed to update profile');
    }

    return response.data.organisation;
  },

  /**
   * Get all admins for an organisation
   * GET /api/v2/org/:organisationId/admins
   */
  getOrgAdmins: async (organisationId) => {
    return webApiClient.get(`/api/v2/org/${organisationId}/admins`);
  },

  /**
   * Add a user as an org admin (owner only)
   * POST /api/v2/org/:organisationId/admins
   */
  addOrgAdmin: async (organisationId, accountId, role = "helper") => {
    return webApiClient.post(`/api/v2/org/${organisationId}/admins`, { accountId, role });
  },

  /**
   * Change the role of an existing team member (owner only)
   * PUT /api/v2/org/:organisationId/admins/:accountId/role
   */
  updateOrgAdminRole: async (organisationId, accountId, role) => {
    return webApiClient.put(`/api/v2/org/${organisationId}/admins/${accountId}/role`, { role });
  },

  /**
   * Remove a user from org admins (owner only)
   * DELETE /api/v2/org/:organisationId/admins/:accountId
   */
  removeOrgAdmin: async (organisationId, accountId) => {
    return webApiClient.delete(`/api/v2/org/${organisationId}/admins/${accountId}`);
  },

  /**
   * Get organisation branding
   * GET /api/v2/org/branding
   */
  getBranding: () => webApiClient.get('/api/v2/org/branding'),

  /**
   * Update organisation branding
   * PUT /api/v2/org/branding
   */
  updateBranding: (brandingData) => webApiClient.put('/api/v2/org/branding', { branding: brandingData }),

  /**
   * Set the Featured Dashboard (hero) card (dot-notation 'settings.dashboardPrimaryCard').
   * A card whose tool is switched off is refused (422 FEATURE_DISABLED_FOR_CARD).
   * PUT /api/v2/org/:organisationId/dashboard-card
   * @param {string|null} dashboardPrimaryCard - 'classes' | 'privateLessons' | 'ticketing' | null
   * @returns {Promise<{ dashboardPrimaryCard: string|null }>}
   */
  updateDashboardCard: async (organisationId, dashboardPrimaryCard) => {
    const response = await webApiClient.put(`/api/v2/org/${organisationId}/dashboard-card`, { dashboardPrimaryCard });

    if (!response.ok || !response.data) {
      throw new Error(response.data?.userMessage || response.data?.message || 'Failed to update dashboard card');
    }

    return response.data;
  },

  /**
   * Toggle whether the school offers class refunds (partial update — dot-notation
   * 'settings.offersRefunds', does not replace the settings subdoc).
   * PUT /api/v2/org/:organisationId/offers-refunds
   */
  updateOffersRefunds: async (organisationId, offersRefunds) => {
    const response = await webApiClient.put(`/api/v2/org/${organisationId}/offers-refunds`, { offersRefunds });

    if (!response.ok || !response.data) {
      throw new Error(response.data?.userMessage || response.data?.message || 'Failed to update refund setting');
    }

    return response.data;
  },

  /**
   * Mark the org's setup checklist as seen so it stops auto-opening on the dashboard.
   * Partial update — dot-notation 'settings.onboardingChecklistSeen', so sibling settings
   * (features, ticketingDefaults, policies) are preserved.
   * PUT /api/v2/org/:organisationId/onboarding-seen
   * @param {string} organisationId
   * @returns {Promise<{ onboardingChecklistSeen: boolean }>}
   */
  updateOnboardingSeen: async (organisationId) => {
    const response = await webApiClient.put(`/api/v2/org/${organisationId}/onboarding-seen`, { onboardingChecklistSeen: true });

    if (!response.ok || !response.data) {
      throw new Error(response.data?.userMessage || response.data?.message || 'Failed to update onboarding state');
    }

    return response.data;
  },

  /**
   * Update the org's base currency.
   *
   * Separate from updateOrgProfile because base currency is bound to the connected
   * account's country: the generic org PATCH is server-side whitelisted and drops
   * `currency`. Free to change before a payout account is linked; afterwards the
   * backend only accepts a currency that still settles in that account's country.
   * PUT /api/v2/org/:organisationId/currency
   * @returns {Promise<{currency: string}>}
   */
  updateOrgCurrency: async (organisationId, currency) => {
    const response = await webApiClient.put(`/api/v2/org/${organisationId}/currency`, { currency });

    if (!response.ok || !response.data?.currency) {
      throw new Error(response.data?.userMessage || response.data?.message || 'Failed to update base currency');
    }

    return response.data;
  },

  /**
   * Update what the org does (partial — any subset of the four keys, dot-notation
   * persisted so sibling settings are preserved). The server derives isDanceSchool /
   * isDanceArtist / isEventOrganiser in the same write, seeds the instructor profile when
   * privateLessons is switched on for an org without one, and clears dashboardPrimaryCard
   * when the hero card's mapped feature is disabled. A domain switched off closes its
   * public surfaces.
   * PATCH /api/v2/org/:organisationId/features
   * @param {object} patch - any subset of { classManagement, privateLessons, ticketing, bulkEmail }
   * @returns {Promise<object>} { features, isDanceSchool, isDanceArtist, isEventOrganiser,
   *                              dashboardPrimaryCard, instructorProfileSeeded }
   */
  updateOrgFeatures: async (organisationId, patch) => {
    const response = await webApiClient.patch(`/api/v2/org/${organisationId}/features`, patch);

    if (!response.ok || !response.data || !response.data.features) {
      throw new Error(response.data?.userMessage || response.data?.message || 'Failed to update features');
    }

    return response.data;
  },

  /**
   * Get the Supplier's buyer-facing policies for both surfaces, resolved (stored
   * values merged over the platform defaults — a never-configured org returns a
   * complete default policy).
   * GET /api/v2/org/:organisationId/policies
   * @returns {Promise<object>} { privateLessons: {...7 fields}, classEnrolments: {...7 fields} }
   */
  getOrgPolicies: async (organisationId) => {
    const response = await webApiClient.get(`/api/v2/org/${organisationId}/policies`);

    if (!response.ok || !response.data || !response.data.policies) {
      throw new Error(response.data?.userMessage || response.data?.message || 'Failed to fetch policies');
    }

    return response.data.policies;
  },

  /**
   * Update the Supplier's buyer-facing policies. At least one surface must be present;
   * each surface carries the 7 policy fields. Persisted via dot-notation per surface so
   * sibling settings are preserved. Do NOT route this through updateOrgSettings (full
   * replacement helper).
   * PUT /api/v2/org/:organisationId/policies
   * @param {object} policies - { privateLessons?: {...7 fields}, classEnrolments?: {...7 fields} }
   * @returns {Promise<object>} { privateLessons: {...}, classEnrolments: {...} } (resolved)
   */
  updateOrgPolicies: async (organisationId, policies) => {
    const response = await webApiClient.put(`/api/v2/org/${organisationId}/policies`, policies);

    if (!response.ok || !response.data || !response.data.policies) {
      throw new Error(response.data?.userMessage || response.data?.message || 'Failed to update policies');
    }

    return response.data.policies;
  },

  /**
   * Get list of dance schools (public directory)
   * @returns {Promise<object>} Response data — callers check .success, read .schools
   * @throws {Error} If fetch fails
   */
  getSchools: async () => {
    const response = await webApiClient.get('/api/v2/schools');

    if (!response.ok || !response.data) {
      throw new Error(response.data?.message || 'Failed to fetch schools');
    }

    return response.data;
  },

  /**
   * Get a profile image as base64 for display
   * Returns raw response — callers check .ok and read .data.imageBase64
   * @param {string} imageKey - Image key (filename)
   * @param {object} [options] - Optional request options (e.g. { timeout: 30000 })
   * @returns {Promise<object>} Raw response
   */
  getProfileImageBase64: async (imageKey, options = {}) => {
    return webApiClient.get(
      `/api/v2/org/profile-image-base64?imageKey=${encodeURIComponent(imageKey)}`,
      null,
      options
    );
  },

  /**
   * Get a profile overlay image as base64 for display (instructor profile photo)
   * Returns raw response — callers check .ok and read .data.imageBase64
   * @param {string} imageKey - Image key (filename)
   * @returns {Promise<object>} Raw response
   */
  getProfileOverlayImageBase64: async imageKey => {
    return webApiClient.get(
      `/api/v2/org/profile-overlay-image-base64?imageKey=${encodeURIComponent(imageKey)}`
    );
  },

  getEmailTemplates: async () => {
    return webApiClient.get('/api/v2/org/email-templates');
  },

  previewEmailTemplate: async (emailType, templateData = {}) => {
    return webApiClient.post(`/api/v2/org/email-templates/${emailType}/preview`, { templateData });
  },

  getEmailTemplateRaw: async (emailType) => {
    return webApiClient.get(`/api/v2/org/email-templates/${emailType}`);
  },

  saveEmailTemplate: async (emailType, subject, bodyHtml) => {
    return webApiClient.put(`/api/v2/org/email-templates/${emailType}`, { subject, bodyHtml });
  },

  resetEmailTemplate: async (emailType) => {
    return webApiClient.post(`/api/v2/org/email-templates/${emailType}/reset`, {});
  },

  sendCommunication: async (payload) => {
    return webApiClient.post('/api/v2/org/communications/send', payload);
  },

  getSentCommunications: async () => {
    return webApiClient.get('/api/v2/org/communications/sent');
  },

  getCommunicationsRecipientCount: async (audienceType, audienceId) => {
    const params = new URLSearchParams({ audienceType });
    if (audienceId) params.set('audienceId', audienceId);
    return webApiClient.get(`/api/v2/org/communications/recipient-count?${params.toString()}`);
  },

  getStudioTemplates: async () => {
    return webApiClient.get('/api/v2/org/studio-templates');
  },

  getStudioTemplate: async (id) => {
    return webApiClient.get(`/api/v2/org/studio-templates/${id}`);
  },

  createStudioTemplate: async (data) => {
    return webApiClient.post('/api/v2/org/studio-templates', data);
  },

  updateStudioTemplate: async (id, data) => {
    return webApiClient.put(`/api/v2/org/studio-templates/${id}`, data);
  },

  deleteStudioTemplate: async (id) => {
    return webApiClient.delete(`/api/v2/org/studio-templates/${id}`);
  },
};
